<template>
  <RevLoadingScreen v-if="pending" :text="i18n(translations.fetchingData)" />

  <div v-else>
    <AddShippingMethod
      class="mb-24"
      :listing-id="listing.legacyId"
      @new-method="refresh"
    />

    <div v-if="missingMarkets.length" class="mb-16 flex items-center">
      <IconWarning class="text-static-danger-hi mr-2" />

      <span class="body-1">
        {{ i18n(translations.missingMarkets) }}
      </span>

      <ul class="ml-4 flex max-w-[36rem] list-none flex-wrap gap-8">
        <li
          v-for="market in missingMarkets"
          :key="market"
          class="border-static-danger-hi body-2 flex items-center justify-between border px-6 py-2"
        >
          <Flag :country-code="market as MarketCountryCode" />

          <span class="text-static-danger-hi body-2-bold ml-1">
            {{ market }}
          </span>
        </li>
      </ul>
    </div>

    <RevTable :collection="shippingMethods" :columns transparent-header>
      <template #body-market="{ item }">
        <div class="body-2 flex items-center justify-between px-6 py-2">
          <Flag
            class="mr-8"
            :country-code="item.market as MarketCountryCode"
            size="m"
          />
          {{ i18n.country(item.market) }}
        </div>
      </template>

      <template #body-level="{ item }">
        <div class="flex flex-col gap-12">
          <div v-for="method in item.shippingMethods" :key="method.id">
            <RevTag :label="item.level.label" variant="secondary" />
          </div>
        </div>
      </template>

      <template #body-provider="{ item }">
        <div class="flex flex-col gap-12">
          <div v-for="method in item.shippingMethods" :key="method.id">
            {{ method.carrier.name }}
          </div>
        </div>
      </template>

      <template #body-deadline="{ item }">
        <div class="flex flex-col gap-12">
          <div v-for="method in item.shippingMethods" :key="method.id">
            {{ i18n(translations.methodDelay, { delay: method.delay }) }}
          </div>
        </div>
      </template>

      <template #body-price="{ item }">
        <div class="flex flex-col gap-12">
          <div v-for="method in item.shippingMethods" :key="method.id">
            {{ i18n.price(method.price) }}
          </div>
        </div>
      </template>

      <template #body-actions="{ item }">
        <div class="flex flex-col gap-12">
          <div v-for="method in item.shippingMethods" :key="method.id">
            <RevButton
              v-show="item.level.code === 'LISTING'"
              size="small"
              variant="secondary"
              @click="handleDeleteMethod(method.id)"
            >
              {{ i18n(translations.delete) }}
            </RevButton>
          </div>
        </div>
      </template>
    </RevTable>
  </div>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { computed } from 'vue'

import { merchantsAPI } from '@backmarket/http-api'
import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import type { Listing } from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants/listings.types'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { RevTable } from '@ds/components/Table'
import { RevTag } from '@ds/components/Tag'
import { IconWarning } from '@ds/icons/IconWarning'

import translations from './Shipping.translations'
import AddShippingMethod from './components/AddShippingMethod.vue'

const i18n = useI18n()
const { openErrorToast, openSuccessToast } = useTheToast()
const props = defineProps<{
  listing: Listing
}>()

const { data, pending, refresh } = useHttpFetch(
  merchantsAPI.getListingShippingMethods,
  {
    pathParams: {
      listingId: props.listing.legacyId,
    },
  },
)

const shippingMethods = computed(() => {
  return data.value?.potentialShippingMethods || []
})

const missingMarkets = computed(() => {
  return data.value?.missingMarkets || []
})

const columns = [
  { key: 'market', label: i18n(translations.market) },
  { key: 'level', label: i18n(translations.shippingLevel) },
  { key: 'provider', label: i18n(translations.shippingProvider) },
  { key: 'deadline', label: i18n(translations.shippingDeadline) },
  { key: 'price', label: i18n(translations.shippingCost) },
  { key: 'actions', label: '' },
]

async function handleDeleteMethod(id: number) {
  try {
    await $httpFetch(merchantsAPI.deleteListingShippingMethod, {
      pathParams: {
        shippingId: id,
      },
    })

    openSuccessToast({
      title: i18n(translations.toastSuccessTitle),
      content: i18n(translations.toastSuccessContent),
    })
    refresh()
  } catch (error) {
    openErrorToast({
      content: getApiErrorMessage(error as unknown as Error),
    })
  }
}
</script>
